/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import errorBoundary from '@tcp/core/src/components/common/hoc/withErrorBoundary/errorBoundary';
import { getViewportInfo, isClient, getSiteId } from '@tcp/core/src/utils';
import GroupedL1NavItems from '@tcp/web/src/components/features/content/Navigation/molecules/GroupedL1NavItems';
import L1NavItem from '../../molecules/L1NavItem';
import style from './NavBar.style';
import L2Panel from '../../molecules/L2Panel';
import Drawer from '../../molecules/Drawer';

const styleL1Headers = (navData) => {
  return navData?.length < 5 && 'center-nav';
};

/**
 * @function cssOrderStyle
 * @param {*} isABTestLoaded
 * @param {*} isL1OrderingEnabled
 * @param {*} cssOrderNos
 * @returns css styles for the order property of each of the elements
 */
const cssOrderStyle = (isABTestLoaded, isL1OrderingEnabled, cssOrderNos) => {
  return (
    isABTestLoaded &&
    isL1OrderingEnabled && (
      <span
        className="cssOrderStyle"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `<style>${cssOrderNos}</style>`,
        }}
      />
    )
  );
};

const checkNavHasImages = (navigationData) => {
  const imagesArr = [];
  navigationData?.forEach((item) => {
    const { displayToCustomer, mainCategory } = item?.categoryContent;
    if (mainCategory) {
      const { categoryFilterImage } = mainCategory;
      if (
        displayToCustomer &&
        categoryFilterImage?.length > 0 &&
        typeof categoryFilterImage[0]?.url_t === 'string' &&
        categoryFilterImage[0]?.url_t !== ''
      ) {
        imagesArr?.push(categoryFilterImage[0]?.url_t);
      }
    }
  });
  return imagesArr?.length > 0;
};

export const trackAnalyticsText = (navL1Item, isNewNavigationImagesEnabled) => {
  const { isDesktop } = getViewportInfo() || {};
  if (isDesktop) {
    if (isNewNavigationImagesEnabled) {
      return `topmenu-withimage-home-${navL1Item.categoryContent.name.toLowerCase()}`;
    }
    return `topmenu-home-${navL1Item.categoryContent.name.toLowerCase()}`;
  }
  return `hamburger-${
    isNewNavigationImagesEnabled ? `withimage-home-` : `home-`
  }${navL1Item.categoryContent.name.toLowerCase()}`;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const groupNavigationData = (navigationData) => {
  const result = [];
  let highestSequence = 0;
  const itemsToBeAdded = {};

  navigationData?.forEach((item, index) => {
    const { displayToCustomer, groupIdentifier } = item.categoryContent;
    if (!displayToCustomer) return;

    const updatedItem = { ...item, index };

    const groupParts = groupIdentifier?.split(':');

    const itemsToBeAddedKeys = Object.keys(itemsToBeAdded);

    itemsToBeAddedKeys?.forEach((seq) => {
      if (Number(seq) <= highestSequence + 1) {
        result.push(itemsToBeAdded[seq]);
        delete itemsToBeAdded[seq];
        highestSequence = Number(seq);
      }
    });

    if (groupParts.length > 1) {
      const sequence = parseInt(groupParts[0], 10);
      const groupName = groupParts[1];
      const resultIndex = result.findIndex(
        (navItem) => navItem.sequence && navItem.sequence === sequence
      );

      if (sequence <= highestSequence + 1) {
        if (resultIndex < 0) {
          result.push({
            isGroupedItems: true,
            dataList: [updatedItem],
            sequence,
            groupName,
          });
          highestSequence = sequence;
        } else {
          result[resultIndex].dataList.push(updatedItem);
        }
        return;
      }

      if (!itemsToBeAdded[sequence]) {
        itemsToBeAdded[sequence] = {
          isGroupedItems: true,
          dataList: [updatedItem],
          sequence,
          groupName,
        };
      } else {
        itemsToBeAdded[sequence].dataList.push(updatedItem);
      }
    } else {
      result.push({
        isGroupedItems: false,
        data: updatedItem,
      });
    }
  });

  const valuesArray = Object.values(itemsToBeAdded);
  if (valuesArray?.length > 0) {
    valuesArray.sort((a, b) => a.sequence - b.sequence);
    result.push(...valuesArray);
  }
  return result;
};

const renderL1ItemMobileRedesign = ({
  navL1Item,
  isNewNavigationImagesEnabled,
  setSelectedL1Index,
  openL2Drawer,
  removeL1Focus,
  trackNavigation,
  isShowNavAnimation,
  showCondensedHeader,
  brandId,
  navigationData,
  isNavSizePromoEnabled,
  brandThemeColor,
  openDrawer,
  closeDrawer,
  hideL2Drawer,
  visibleItemCount,
  openL3Drawer,
  hideL3Drawer,
  l3Drawer,
  closeNav,
  isShopBySizeAbTestOff,
  disableBabyL2Nesting,
  isOutfitPLPEnabled,
  isGuestFavEnabled,
  setHideDrawerInfoSection,
  additionalClassName = ' nav-bar-l1-item-mobile-redesign',
}) => {
  const { index, categoryContent } = navL1Item || {};
  const { asPath, mainCategory } = categoryContent || {};

  let categoryLayout = [];
  let size = '';
  const settings = {};
  const stringId = index.toString();
  if (mainCategory) {
    const {
      categoryLayout: catLayout,
      sizesRange,
      set,
      categoryLayoutMobile: catLayoutMobile,
    } = mainCategory || {};
    categoryLayout = catLayout;
    const range = sizesRange && sizesRange[0];
    size = range && range.text;
    if (set) {
      set.forEach(({ key, val }) => {
        settings[key] = val;
      });
    }
    categoryLayout = !isEmpty(catLayoutMobile) ? catLayoutMobile : catLayout;
  }
  let topNavigationAnalyticsData;
  if (isClient()) {
    topNavigationAnalyticsData = trackAnalyticsText(navL1Item, isNewNavigationImagesEnabled);
  }
  return (
    <L1NavItem
      dataLocator={`l1menu_link_mobile_redesign_${index}`}
      clickData={topNavigationAnalyticsData}
      customClickEvent="event82"
      index={index}
      key={`l1menu_link_mobile_redesign_${stringId}`}
      sizesRange={size}
      linkOverrideUrl={settings.sourceURL}
      onClick={() => {
        setTimeout(() => {
          setSelectedL1Index(index);
          openL2Drawer(`l2-drawer-${stringId}`)();
          const drawerElement = document.getElementById('tcp-nav-drawer');
          if (drawerElement) {
            drawerElement.scrollTop = 0;
          }
        }, 0);
      }}
      removeL1Focus={removeL1Focus}
      trackNavigation={trackNavigation}
      {...navL1Item}
      isShowNavAnimation={isShowNavAnimation}
      showCondensedHeader={showCondensedHeader}
      isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
      brandId={brandId}
      checkNavHasImages={checkNavHasImages(navigationData)}
      url={`/${getSiteId()}${asPath}`}
      isNavSizePromoEnabled={isNavSizePromoEnabled}
      brandThemeColor={brandThemeColor}
      shouldRenderMobileNavRedesignedView
      additionalClassName={`${additionalClassName} hide-on-desktop`}
      setHideDrawerInfoSection={setHideDrawerInfoSection}
    >
      <Drawer
        id={`l2-drawer-${stringId}`}
        drawerElemId={`drawer-wrapper-${index}`}
        small
        medium
        open={openDrawer}
        close={closeDrawer}
        width={{
          small: '343px',
          medium: '343px',
          large: '100%',
        }}
        position={{
          top: 0,
          left: 0,
        }}
        height="100%"
        navigationLevel="l2"
        isShowNavAnimation={isShowNavAnimation}
        isMobileNavigationRedesignEnabled
      >
        <L2Panel
          categoryLayout={categoryLayout}
          panelData={navL1Item.subCategories}
          name={categoryContent.name}
          l1Id={categoryContent.id}
          hideL2Drawer={hideL2Drawer(`l2-drawer-${stringId}`)}
          className="nav-bar-l2"
          l1Index={index}
          visibleItemCount={visibleItemCount}
          openL3Drawer={openL3Drawer}
          hideL3Drawer={hideL3Drawer}
          l3Drawer={l3Drawer}
          closeNav={closeNav}
          analyticsData={topNavigationAnalyticsData}
          customClickEvent="event82"
          trackNavigation={trackNavigation}
          isShopBySizeAbTestOff={isShopBySizeAbTestOff}
          disableBabyL2Nesting={disableBabyL2Nesting}
          isShowNavAnimation={isShowNavAnimation}
          navL1ItemLength={Object.keys(navL1Item?.subCategories)?.length}
          setSelectedL1Index={setSelectedL1Index}
          setHideDrawerInfoSection={setHideDrawerInfoSection}
          isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
          isOutfitPLPEnabled={isOutfitPLPEnabled}
          isGuestFavEnabled={isGuestFavEnabled}
          brandThemeColor={brandThemeColor}
          shouldRenderMobileNavRedesignedView
          additionalClassName={' nav-bar-l2-mobile-redesign'}
        />
      </Drawer>
    </L1NavItem>
  );
};

const renderGroupedL1NavItems = ({
  groupedItems,
  isNewNavigationImagesEnabled,
  setSelectedL1Index,
  openL2Drawer,
  removeL1Focus,
  trackNavigation,
  isShowNavAnimation,
  showCondensedHeader,
  brandId,
  navigationData,
  isNavSizePromoEnabled,
  brandThemeColor,
  openDrawer,
  closeDrawer,
  hideL2Drawer,
  visibleItemCount,
  openL3Drawer,
  hideL3Drawer,
  l3Drawer,
  closeNav,
  isShopBySizeAbTestOff,
  disableBabyL2Nesting,
  isOutfitPLPEnabled,
  isGuestFavEnabled,
  setHideDrawerInfoSection,
}) => {
  const { sequence, dataList, groupName } = groupedItems || {};
  return (
    <GroupedL1NavItems
      key={`${sequence}:${groupName}`}
      groupName={groupName}
      defaultOpen={sequence < 3}
    >
      {dataList.map((dataItem) => {
        return renderL1ItemMobileRedesign({
          navL1Item: dataItem,
          isNewNavigationImagesEnabled,
          setSelectedL1Index,
          openL2Drawer,
          removeL1Focus,
          trackNavigation,
          isShowNavAnimation,
          showCondensedHeader,
          brandId,
          navigationData,
          isNavSizePromoEnabled,
          brandThemeColor,
          openDrawer,
          closeDrawer,
          hideL2Drawer,
          visibleItemCount,
          openL3Drawer,
          hideL3Drawer,
          l3Drawer,
          closeNav,
          isShopBySizeAbTestOff,
          disableBabyL2Nesting,
          isOutfitPLPEnabled,
          isGuestFavEnabled,
          setHideDrawerInfoSection,
          additionalClassName: ' nav-bar-l1-item-mobile-redesign grouped-l1-item',
        });
      })}
    </GroupedL1NavItems>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const NavBar = (props) => {
  const {
    setSelectedL1Index,
    visibleItemCount,
    nav: navigationData,
    cssOrderNos,
    isABTestLoaded,
    isL1OrderingEnabled,
    className,
    openL2Drawer,
    openDrawer,
    closeDrawer,
    hideL2Drawer,
    openL3Drawer,
    hideL3Drawer,
    l3Drawer,
    removeL1Focus,
    closeNav,
    trackNavigation,
    isShopBySizeAbTestOff,
    disableBabyL2Nesting,
    isShowNavAnimation,
    brandId,
    showCondensedHeader,
    isNewNavigationImagesEnabled,
    checkNavImagesFlag,
    isOutfitPLPEnabled,
    isNavSizePromoEnabled,
    isGuestFavEnabled,
    brandThemeColor,
    isMobileNavigationRedesignEnabled,
    setHideDrawerInfoSection,
  } = props;
  const { isMobile, isTablet } = (isClient() && getViewportInfo()) || {};

  useEffect(() => {
    checkNavImagesFlag(checkNavHasImages(navigationData));
  }, [navigationData]);

  const navDataGrouping = useMemo(() => {
    if (isMobileNavigationRedesignEnabled) {
      return groupNavigationData(navigationData);
    }
    return [];
  }, [navigationData, isMobileNavigationRedesignEnabled]);

  return (
    <React.Fragment>
      {cssOrderStyle(isABTestLoaded, isL1OrderingEnabled, cssOrderNos)}
      <ul
        className={`${className} nav-bar-l1 content-wrapper  ${styleL1Headers(navigationData)}`}
        role="menubar"
      >
        {isNewNavigationImagesEnabled && !showCondensedHeader && (
          <hr className={'image-bar-separator'} />
        )}
        {navigationData &&
          // eslint-disable-next-line sonarjs/cognitive-complexity
          navigationData.map((navL1Item, index) => {
            const { displayToCustomer, asPath } = navL1Item.categoryContent;
            if (!displayToCustomer) return null;

            let categoryLayout = [];
            let size = '';
            const settings = {};
            const stringId = index.toString();
            if (navL1Item.categoryContent.mainCategory) {
              const { mainCategory } = navL1Item.categoryContent;
              const {
                categoryLayout: catLayout,
                sizesRange,
                set,
                categoryLayoutMobile: catLayoutMobile,
              } = mainCategory || {};
              categoryLayout = catLayout;
              const range = sizesRange && sizesRange[0];
              size = range && range.text;
              if (set) {
                set.forEach(({ key, val }) => {
                  settings[key] = val;
                });
              }
              if (isMobile || isTablet) {
                categoryLayout = !isEmpty(catLayoutMobile) ? catLayoutMobile : catLayout;
              }
            }
            let topNavigationAnalyticsData;
            if (isClient()) {
              topNavigationAnalyticsData = trackAnalyticsText(
                navL1Item,
                isNewNavigationImagesEnabled
              );
            }
            return (
              <L1NavItem
                dataLocator={`l1menu_link_${index}`}
                clickData={topNavigationAnalyticsData}
                customClickEvent="event82"
                index={index}
                key={`l1menu_link_${stringId}`}
                sizesRange={size}
                linkOverrideUrl={settings.sourceURL}
                // eslint-disable-next-line sonarjs/no-identical-functions
                onClick={() => {
                  // eslint-disable-next-line sonarjs/no-identical-functions
                  setTimeout(() => {
                    setSelectedL1Index(index);
                    openL2Drawer(`l2-drawer-${stringId}`)();
                    const drawerElement = document.getElementById('tcp-nav-drawer');
                    if (drawerElement) {
                      drawerElement.scrollTop = 0;
                    }
                  }, 0);
                }}
                // showOnlyOnApp={typeof settings.showOnlyOnApp !== 'undefined'}
                removeL1Focus={removeL1Focus}
                trackNavigation={trackNavigation}
                {...navL1Item}
                isShowNavAnimation={isShowNavAnimation}
                showCondensedHeader={showCondensedHeader}
                isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
                brandId={brandId}
                checkNavHasImages={checkNavHasImages(navigationData)}
                url={`/${getSiteId()}${asPath}`}
                isNavSizePromoEnabled={isNavSizePromoEnabled}
                brandThemeColor={brandThemeColor}
                additionalClassName={
                  isMobileNavigationRedesignEnabled ? ' hide-on-tablet hide-on-mobile' : ''
                }
              >
                <Drawer
                  id={`l2-drawer-${stringId}`}
                  drawerElemId={`drawer-wrapper-${index}`}
                  small
                  medium
                  open={openDrawer}
                  close={closeDrawer}
                  width={{
                    small: '314px',
                    medium: '314px',
                    large: '100%',
                  }}
                  position={{
                    top: 0,
                    left: 0,
                  }}
                  height="100%"
                  navigationLevel="l2"
                  isShowNavAnimation={isShowNavAnimation}
                >
                  <L2Panel
                    categoryLayout={categoryLayout}
                    panelData={navL1Item.subCategories}
                    name={navL1Item.categoryContent.name}
                    l1Id={navL1Item.categoryContent.id}
                    hideL2Drawer={hideL2Drawer(`l2-drawer-${stringId}`)}
                    className="nav-bar-l2"
                    l1Index={index}
                    visibleItemCount={visibleItemCount}
                    openL3Drawer={openL3Drawer}
                    hideL3Drawer={hideL3Drawer}
                    l3Drawer={l3Drawer}
                    closeNav={closeNav}
                    analyticsData={topNavigationAnalyticsData}
                    customClickEvent="event82"
                    trackNavigation={trackNavigation}
                    isShopBySizeAbTestOff={isShopBySizeAbTestOff}
                    disableBabyL2Nesting={disableBabyL2Nesting}
                    isShowNavAnimation={isShowNavAnimation}
                    navL1ItemLength={Object.keys(navL1Item?.subCategories)?.length}
                    setSelectedL1Index={setSelectedL1Index}
                    isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
                    isOutfitPLPEnabled={isOutfitPLPEnabled}
                    isGuestFavEnabled={isGuestFavEnabled}
                    brandThemeColor={brandThemeColor}
                  />
                </Drawer>
              </L1NavItem>
            );
          })}
        {isMobileNavigationRedesignEnabled &&
          navDataGrouping?.map((item) => {
            if (item.isGroupedItems) {
              return renderGroupedL1NavItems({
                groupedItems: item,
                isNewNavigationImagesEnabled,
                setSelectedL1Index,
                openL2Drawer,
                removeL1Focus,
                trackNavigation,
                isShowNavAnimation,
                showCondensedHeader,
                brandId,
                navigationData,
                isNavSizePromoEnabled,
                brandThemeColor,
                openDrawer,
                closeDrawer,
                hideL2Drawer,
                visibleItemCount,
                openL3Drawer,
                hideL3Drawer,
                l3Drawer,
                closeNav,
                isShopBySizeAbTestOff,
                disableBabyL2Nesting,
                isOutfitPLPEnabled,
                isGuestFavEnabled,
                setHideDrawerInfoSection,
              });
            }
            return renderL1ItemMobileRedesign({
              navL1Item: item.data,
              isNewNavigationImagesEnabled,
              setSelectedL1Index,
              openL2Drawer,
              removeL1Focus,
              trackNavigation,
              isShowNavAnimation,
              showCondensedHeader,
              brandId,
              navigationData,
              isNavSizePromoEnabled,
              brandThemeColor,
              openDrawer,
              closeDrawer,
              hideL2Drawer,
              visibleItemCount,
              openL3Drawer,
              hideL3Drawer,
              l3Drawer,
              closeNav,
              isShopBySizeAbTestOff,
              disableBabyL2Nesting,
              isOutfitPLPEnabled,
              isGuestFavEnabled,
              setHideDrawerInfoSection,
            });
          })}
      </ul>
    </React.Fragment>
  );
};

NavBar.propTypes = {
  nav: PropTypes.arrayOf(PropTypes.shape({})),
  setSelectedL1Index: PropTypes.func.isRequired,
  visibleItemCount: PropTypes.number.isRequired,
  cssOrderNos: PropTypes.string,
  className: PropTypes.string.isRequired,
  mainCategory: PropTypes.shape({}),
  openL2Drawer: PropTypes.func.isRequired,
  hideL2Drawer: PropTypes.func.isRequired,
  closeNav: PropTypes.func.isRequired,
  openDrawer: PropTypes.string,
  closeDrawer: PropTypes.bool,
  openL3Drawer: PropTypes.func.isRequired,
  hideL3Drawer: PropTypes.func.isRequired,
  l3Drawer: PropTypes.shape({}),
  removeL1Focus: PropTypes.bool,
  trackNavigation: PropTypes.func.isRequired,
  isShopBySizeAbTestOff: PropTypes.bool,
  disableBabyL2Nesting: PropTypes.bool,
  isABTestLoaded: PropTypes.bool,
  isL1OrderingEnabled: PropTypes.bool,
  isShowNavAnimation: PropTypes.bool,
  brandId: PropTypes.string.isRequired,
  isNewNavigationImagesEnabled: PropTypes.bool,
  showCondensedHeader: PropTypes.bool,
  checkNavImagesFlag: PropTypes.func,
  isNavSizePromoEnabled: PropTypes.bool,
  isGuestFavEnabled: PropTypes.bool,
};

NavBar.defaultProps = {
  mainCategory: {},
  disableBabyL2Nesting: false,
  cssOrderNos: '',
  isABTestLoaded: false,
  isL1OrderingEnabled: false,
  isShowNavAnimation: false,
  removeL1Focus: undefined,
  isShopBySizeAbTestOff: undefined,
  openDrawer: undefined,
  closeDrawer: undefined,
  l3Drawer: undefined,
  isNewNavigationImagesEnabled: false,
  showCondensedHeader: false,
  checkNavImagesFlag: () => {},
  isNavSizePromoEnabled: false,
  isGuestFavEnabled: false,
};

export { NavBar as NavBarVanilla };
export default errorBoundary(withStyles(NavBar, style));
